import TWEEN from '@tweenjs/tween.js';
import * as THREE from 'three';
import { SockType } from './new-sock';
import sockConfig from './new-sock-config';

export interface AnimationOption {
  camera: THREE.Camera;
  orbitControls: any;
  object?: THREE.Object3D;
}

export interface Position {
  x: number;
  y: number;
  z: number;
}

class Animation {
  private _type: SockType;
  private _camera: THREE.Camera;
  private _object?: THREE.Object3D;
  private _orbitControls: any;

  constructor(option: AnimationOption, type: SockType) {
    this._camera = option.camera;
    this._orbitControls = option.orbitControls;
    this._object = option.object;
    this._type = type;
    this._animationPlay();
  }

  /** 播放动画 */
  private _animationPlay() {
    TWEEN.update();
    this._orbitControls.update();
    requestAnimationFrame(this._animationPlay.bind(this));
  }

  /** 锁定控制器控制角度 */
  private _lockControlsAngle() {
    if (this._type === SockType.SOCK) {
      const lockAngle = this._orbitControls.getPolarAngle();
      this._orbitControls.maxPolarAngle = lockAngle;
      this._orbitControls.minPolarAngle = lockAngle;
    }
  }

  /** 初始动画 */
  public initial(position: Position, duration: number = 1000, callback?: Function) {
    this._orbitControls.maxPolarAngle = Math.PI;
    this._orbitControls.minPolarAngle = 0;
    const nextPosition = this._camera.position.clone();
    nextPosition.set(position.x, position.y, position.z);
    const tween = new TWEEN.Tween(this._camera.position).to(nextPosition, duration).easing(TWEEN.Easing.Cubic.Out).start();
    tween.onComplete(() => {
      this._lockControlsAngle();
      callback && callback();
    });
  }

  /** 放大动画 */
  public amplify(duration: number = 1000, callback?: Function) {
    this._orbitControls.maxPolarAngle = Math.PI;
    this._orbitControls.minPolarAngle = 0;

    const amplifyPosition: any = sockConfig[this._type].amplify;

    const newPosition = this._camera.position.clone();
    const newTarget = this._orbitControls.target.clone();
    let tween;
    const camera = amplifyPosition.camera;
    newPosition.set(camera.x, camera.y, camera.z);

    const isBoxer = this._type === SockType.BOXER || this._type === SockType.KYBOXER || this._type === SockType.BOXER435 || this._type === SockType.BOXER395Z || this._type === SockType.BOXER015Z;

    if (isBoxer) {
      tween = new TWEEN.Tween(this._camera.position).to(newPosition, duration).easing(TWEEN.Easing.Cubic.Out).start();
    } else if (this._type === SockType.SOCK) {
      const control = amplifyPosition.control;
      newTarget.set(control.x, control.y, control.z);
      new TWEEN.Tween(this._orbitControls.target).to(newTarget, duration).easing(TWEEN.Easing.Cubic.Out).start();

      const cameraUp = amplifyPosition.cameraUp;
      new TWEEN.Tween(this._camera.up).to(cameraUp, duration).easing(TWEEN.Easing.Cubic.Out).start();

      tween = new TWEEN.Tween(this._camera.position).to(newPosition, duration).easing(TWEEN.Easing.Cubic.Out).start();
    }

    tween.onComplete(() => {
      callback && callback();
    });
  }

  /** 刻字确认动画 */
  public confirm(duration: number = 1000, callback?: Function) {
    if (this._object) {
      const newPosition = this._object.position.clone();
      const isBoxer = this._type === SockType.BOXER || this._type === SockType.BOXER435 || this._type === SockType.BOXER395Z || this._type === SockType.BOXER015Z;
      let tween;
      if (isBoxer) {
        const confirmPosition = sockConfig.BOXER.confirm.mesh;
        newPosition.set(confirmPosition.x, confirmPosition.y, confirmPosition.z);
        tween = new TWEEN.Tween(this._object.position).to(newPosition, duration).easing(TWEEN.Easing.Cubic.Out).start();
      }
      tween && tween.onComplete(() => {
        callback && callback();
      });
    }
  }

  /** 恢复动画 */
  public reSize(position: any, target: any, duration: number = 1000, callback?: Function) {
    this._orbitControls.maxPolarAngle = Math.PI;
    this._orbitControls.minPolarAngle = 0;
    const newPosition = position;
    const newTarget = target;
    const newCameraUp = { x: 0, y: 1, z: 0 };
    const isBoxer = this._type === SockType.BOXER || this._type === SockType.BOXER435 || this._type === SockType.BOXER395Z || this._type === SockType.BOXER015Z;
    if (isBoxer && this._object) {
      const meshOrigin = sockConfig.BOXER.initialObject;
      const newMeshPosition = this._object.position.clone();
      newMeshPosition.set(meshOrigin.x, meshOrigin.y, meshOrigin.z);
      new TWEEN.Tween(this._object.position).to(newMeshPosition, duration).easing(TWEEN.Easing.Cubic.Out).start();
    }
    new TWEEN.Tween(this._camera.position).to(newPosition, duration).easing(TWEEN.Easing.Cubic.Out).start();
    new TWEEN.Tween(this._orbitControls.target).to(newTarget, duration).easing(TWEEN.Easing.Cubic.Out).start();
    const tween = new TWEEN.Tween(this._camera.up).to(newCameraUp, duration).easing(TWEEN.Easing.Cubic.Out).start();
    tween.onComplete(() => {
      this._lockControlsAngle();
      callback && callback();
    });
  }
}

export default Animation;
